import { useEffect, useMemo, useState } from 'react';
import type { BookingProfessional, ConsultedMember } from '../types';
import { useRootData } from '@modules/root';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
    confirmationDetailsSchema,
    type ConfirmationDetails,
} from './ConfirmationPhase';
import { ResultPhase } from './ResultPhase';
import { BookingPhase } from './BookingPhase';
import { ConfirmationPhase } from './ConfirmationPhase';
import { ConsultantDisplay } from './ConsultantDisplay';
import { getTimezoneShort } from '../utils/calendar';

dayjs.extend(utc);
dayjs.extend(timezone);

interface FreeDay {
    date: string;
    freeSlots: Array<{ label: string; timestamp: number }>;
}

interface BookingScheduleControlsProps {
    timezoneName: string;
    availableTimezones: string[];
    onTimezoneChange: (timezone: string) => void;
    selectedDateTimestamp: number | null;
    onCalendarSelect: (timestamp: number | null | undefined) => void;
    meetingType?: 'one-on-one' | 'random' | null;
    consultedMember: ConsultedMember | null;
    professional?: BookingProfessional | null;
    isProfessionalLoading?: boolean;
    freeDays: FreeDay[];
    durations: number[];
    selectedDuration: number;
    onDurationChange: (duration: number) => void;
    isDurationDisabled?: boolean;
    freeHours: string[];
    selectedSlotTimestamp: number | null;
    onTimeSelect: (timestamp: number) => void;
    normalizedSelectedSlotTimestamp: number | null;
    selectedSlotLabel: string;
    statusMessage?: string;
    success?: boolean;
    failure?: boolean;
    onSubmit: (details: ConfirmationDetails) => void;
    bookingDisabled: boolean;
    confirmButtonLabel: string;
    userEmailLabel: string;
    submissionState: 'idle' | 'loading' | 'submitting';
    timePickerVariant?: 'list' | 'listCompact';
    timePickerStartHour: number;
    timePickerEndHour: number;
    timePickerStepMinutes: number;
    onCloseConfirmation?: () => void;
    onConfirmationPhaseChange?: (isConfirmation: boolean) => void;
    initialBookingDetails?: {
        clientName?: string;
        clientEmail?: string;
        clientCompany?: string;
    };
    isReschedule?: boolean;
    eventId?: string;
    onCancel?: () => void;
    onReschedule?: () => void;
    showSpamHint?: boolean;
}
/**
 * Booking schedule controls component
 * Displays the booking schedule controls for the booking page
 * Includes the consultant display, booking phase, confirmation phase, and result phase
 * Handles the booking process and displays the result of the booking
 */
export const BookingScheduleControls = ({
    timezoneName,
    availableTimezones,
    onTimezoneChange,
    selectedDateTimestamp,
    onCalendarSelect,
    meetingType: rawMeetingType,
    consultedMember,
    professional,
    isProfessionalLoading = false,
    freeDays,
    durations,
    selectedDuration,
    onDurationChange,
    isDurationDisabled = false,
    freeHours,
    onTimeSelect,
    normalizedSelectedSlotTimestamp,
    selectedSlotLabel,
    statusMessage,
    success = false,
    failure = false,
    onSubmit,
    bookingDisabled,
    confirmButtonLabel,
    userEmailLabel,
    submissionState,
    timePickerVariant = 'list',
    timePickerStartHour = 9,
    timePickerEndHour = 16,
    timePickerStepMinutes = 60,
    onConfirmationPhaseChange,
    initialBookingDetails,
    isReschedule = false,
    eventId,
    onCancel,
    onReschedule,
    showSpamHint = false,
}: BookingScheduleControlsProps) => {
    const meetingType =
        rawMeetingType === 'one-on-one' ? 'one-on-one' : 'random';

    const consultantName =
        meetingType === 'one-on-one'
            ? (consultedMember?.name ?? 'Team member')
            : (consultedMember?.name ?? professional?.title ?? 'Our team');

    const consultantSubtitle =
        meetingType === 'one-on-one'
            ? (consultedMember?.professionalName ?? 'Client Director')
            : (consultedMember?.professionalName ??
              professional?.teaser ??
              'Client Director');

    const consultantImage =
        meetingType === 'one-on-one'
            ? (consultedMember?.avatarUrl ?? null)
            : (professional?.libraryThumb?.thumbImageFile ?? null);

    const consultantLogoUrl =
        meetingType === 'one-on-one'
            ? (consultedMember?.professionalLogoUrl ?? null)
            : (consultedMember?.professionalLogoUrl ??
              professional?.companyLogo ??
              null);

    const consultantInitial =
        meetingType === 'one-on-one'
            ? consultantName
                  .split(/\s+/)
                  .filter(Boolean)
                  .map((word) => word.charAt(0).toUpperCase())
                  .slice(0, 2)
                  .join('') || '?'
            : (() => {
                  const title = professional?.title ?? consultantName;
                  if (!title) {
                      return '?';
                  }
                  const trimmedTitle = title.trim();
                  if (!trimmedTitle) {
                      return '?';
                  }
                  const lowerTrimmed = trimmedTitle.toLowerCase();
                  const professionalPrefix = 'professional ';
                  if (lowerTrimmed.startsWith(professionalPrefix)) {
                      const withoutPrefix = trimmedTitle
                          .slice(professionalPrefix.length)
                          .trimStart();
                      return withoutPrefix || trimmedTitle;
                  }
                  return trimmedTitle;
              })();

    const consultantLogoBackgroundColor =
        meetingType === 'one-on-one'
            ? undefined
            : (professional?.logoBackgroundColor ?? 'white');

    const { user } = useRootData();

    const [clientName, setClientName] = useState<string>(() => {
        if (initialBookingDetails?.clientName) {
            return initialBookingDetails.clientName;
        }
        if (user.__typename === 'Me') {
            return [user.FirstName, user.LastName]
                .filter(Boolean)
                .join(' ')
                .trim();
        }
        return '';
    });
    const [clientEmail, setClientEmail] = useState<string>(() => {
        if (initialBookingDetails?.clientEmail) {
            return initialBookingDetails.clientEmail;
        }
        return user.__typename === 'Me' ? (user.Email ?? '') : '';
    });
    const [clientCompany, setClientCompany] = useState<string>(() => {
        if (initialBookingDetails?.clientCompany) {
            return initialBookingDetails.clientCompany;
        }
        if (user.__typename === 'Me') {
            // If user has more than one professional, default to empty string
            if (user.Professionals.length > 1) {
                return '';
            }
            return user.Professionals[0]?.Title ?? user.Company ?? '';
        }
        return '';
    });
    const [isConfirmationPhase, setIsConfirmationPhase] =
        useState<boolean>(false);
    const [showValidationErrors, setShowValidationErrors] =
        useState<boolean>(false);

    useEffect(() => {
        // Don't update if rescheduling with initial booking details
        if (isReschedule && initialBookingDetails) {
            if (initialBookingDetails.clientName) {
                setClientName(initialBookingDetails.clientName);
            }
            if (initialBookingDetails.clientEmail) {
                setClientEmail(initialBookingDetails.clientEmail);
            }
            if (initialBookingDetails.clientCompany) {
                setClientCompany(initialBookingDetails.clientCompany);
            }
            return;
        }

        if (user.__typename !== 'Me') {
            return;
        }

        const nextName = [user.FirstName, user.LastName]
            .filter(Boolean)
            .join(' ')
            .trim();
        setClientName(nextName);
        setClientEmail(user.Email ?? '');
        // If user has more than one professional, default to empty string
        if (user.Professionals.length > 1) {
            setClientCompany('');
        } else {
            setClientCompany(
                user.Professionals[0]?.Title ?? user.Company ?? '',
            );
        }
    }, [user, isReschedule, initialBookingDetails]);

    useEffect(() => {
        if (!normalizedSelectedSlotTimestamp) {
            setIsConfirmationPhase(false);
        }
    }, [normalizedSelectedSlotTimestamp]);

    useEffect(() => {
        if (success) {
            setIsConfirmationPhase(false);
        }
    }, [success]);

    useEffect(() => {
        if (!isConfirmationPhase) {
            setShowValidationErrors(false);
        }
    }, [isConfirmationPhase]);

    useEffect(() => {
        onConfirmationPhaseChange?.(isConfirmationPhase);
    }, [isConfirmationPhase, onConfirmationPhaseChange]);

    const handleConfirmationClose = () => {
        if (typeof window === 'undefined') {
            return;
        }
        window.location.reload();
    };

    const handleDateSelect = (timestamp: number) => {
        setIsConfirmationPhase(false);
        if (typeof onCalendarSelect === 'function') {
            onCalendarSelect(timestamp);
        }
    };

    const hasSelectedSlot =
        typeof selectedSlotLabel === 'string' &&
        selectedSlotLabel.trim().length > 0;

    const slotLabel = hasSelectedSlot
        ? selectedSlotLabel
        : 'Select a date and time to continue';

    const isProcessing =
        submissionState === 'submitting' || submissionState === 'loading';
    const isLoggedIn = user.__typename === 'Me';
    // Name and email are disabled for logged-in users or when rescheduling, but company can always be edited unless rescheduling
    const userInputsDisabled = isProcessing || isLoggedIn || isReschedule;
    const companyInputDisabled = isProcessing || isReschedule;

    const validationResult = useMemo(
        () =>
            confirmationDetailsSchema.safeParse({
                clientName,
                clientEmail,
                clientCompany,
            }),
        [clientName, clientEmail, clientCompany],
    );

    const fieldErrors = validationResult.success
        ? {}
        : validationResult.error.flatten().fieldErrors;

    useEffect(() => {
        if (showValidationErrors && validationResult.success) {
            setShowValidationErrors(false);
        }
    }, [showValidationErrors, validationResult.success]);

    const confirmDisabled = bookingDisabled;

    const buttonLabel = isProcessing ? 'Booking...' : confirmButtonLabel;
    const showStatusMessage = Boolean(statusMessage) && !success;

    const formattedDate = normalizedSelectedSlotTimestamp
        ? dayjs(normalizedSelectedSlotTimestamp)
              .tz(timezoneName)
              .format('D MMMM, YYYY')
        : '';

    const formattedTime = normalizedSelectedSlotTimestamp
        ? dayjs(normalizedSelectedSlotTimestamp)
              .tz(timezoneName)
              .format('hh:mm a')
              .toUpperCase()
        : '';

    const timezoneAbbr = getTimezoneShort(
        timezoneName,
        normalizedSelectedSlotTimestamp,
    );

    const meetingSummary = (
        <div className="relative -top-16 w-90 text-center">
            <div className="mx-auto w-54 text-4.5">
                You're requesting a meeting with {consultantName} for:
            </div>
            {hasSelectedSlot ? (
                <>
                    <div className="mt-2 text-7 font-medium leading-[1.3] text-gray-900">
                        {formattedDate}
                    </div>
                    <div className="mt-2 text-7 font-medium leading-[1.3] text-gray-900">
                        {formattedTime} {timezoneAbbr}
                    </div>
                </>
            ) : (
                <div className="mx-auto mt-2 w-70 text-7 font-medium text-gray-900">
                    {slotLabel}
                </div>
            )}
        </div>
    );

    const handleTimeSelectInternal = (timestamp: number) => {
        if (!Number.isFinite(timestamp)) {
            setIsConfirmationPhase(false);
        } else {
            setIsConfirmationPhase(true);
        }
        onTimeSelect(timestamp);
    };

    const bookingContainerClassName = [
        'flex-stretch flex h-170 w-358 overflow-hidden rounded-3 bg-[#fafafa] px-12 pt-12 gap-18 justify-between',
    ].join(' ');

    const handleDurationSelect = (value: string) => {
        setIsConfirmationPhase(false);
        const parsedValue = Number.parseInt(value, 10);
        if (!Number.isNaN(parsedValue)) {
            onDurationChange(parsedValue);
        }
    };

    const handleConfirmationSubmit = () => {
        setShowValidationErrors(true);
        if (confirmDisabled) {
            return;
        }
        if (!validationResult.success) {
            return;
        }
        onSubmit(validationResult.data);
    };

    const bookingContent = (
        <div
            className={bookingContainerClassName}
            style={{ display: success || failure ? 'none' : 'flex' }}
        >
            <ConsultantDisplay
                meetingType={meetingType}
                consultantName={consultantName}
                consultantSubtitle={consultantSubtitle}
                consultantImage={consultantImage}
                consultantLogoUrl={consultantLogoUrl}
                consultantInitial={consultantInitial}
                consultantLogoBackgroundColor={consultantLogoBackgroundColor}
                professionalCategories={professional?.categories ?? undefined}
                isProfessionalLoading={isProfessionalLoading}
                professionalTitle={professional?.title ?? ''}
                profileUrlSegment={professional?.urlSegment ?? ''}
            />
            <div className="">
                {isConfirmationPhase && (
                    <h3 className="text-6 font-medium text-gray-900">
                        Schedule a meeting
                    </h3>
                )}

                <div
                    className="flex h-158 w-225 rounded-6 bg-white p-10"
                    style={{
                        height: isConfirmationPhase ? '36rem' : '40rem',
                        marginTop: isConfirmationPhase ? '1rem' : '0',
                    }}
                >
                    <div
                        className="flex h-full w-full"
                        style={{
                            display: isConfirmationPhase ? 'none' : 'flex',
                        }}
                    >
                        <BookingPhase
                            timezoneName={timezoneName}
                            availableTimezones={availableTimezones}
                            onTimezoneChange={onTimezoneChange}
                            selectedDateTimestamp={selectedDateTimestamp}
                            onDateSelect={handleDateSelect}
                            freeDays={freeDays}
                            durations={durations}
                            selectedDuration={selectedDuration}
                            onDurationChange={handleDurationSelect}
                            isDurationDisabled={isDurationDisabled}
                            freeHours={freeHours}
                            normalizedSelectedSlotTimestamp={
                                normalizedSelectedSlotTimestamp
                            }
                            onTimeSelect={handleTimeSelectInternal}
                            timePickerVariant={timePickerVariant}
                            timePickerStartHour={timePickerStartHour}
                            timePickerEndHour={timePickerEndHour}
                            timePickerStepMinutes={timePickerStepMinutes}
                            meetingSummary={meetingSummary}
                            hasSelectedSlot={hasSelectedSlot}
                        />
                    </div>
                    <div
                        className="flex h-full w-full flex-col"
                        style={{
                            display: isConfirmationPhase ? 'flex' : 'none',
                        }}
                    >
                        <ConfirmationPhase
                            meetingSummary={meetingSummary}
                            clientName={clientName}
                            clientEmail={clientEmail}
                            clientCompany={clientCompany}
                            onClientNameChange={setClientName}
                            onClientEmailChange={setClientEmail}
                            onClientCompanyChange={setClientCompany}
                            userInputsDisabled={userInputsDisabled}
                            companyInputDisabled={companyInputDisabled}
                            userEmailLabel={userEmailLabel}
                            showValidationErrors={showValidationErrors}
                            fieldErrors={fieldErrors}
                            onBack={() => setIsConfirmationPhase(false)}
                            onConfirm={handleConfirmationSubmit}
                            confirmDisabled={confirmDisabled}
                            isProcessing={isProcessing}
                            buttonLabel={buttonLabel}
                            statusMessage={statusMessage}
                            showStatusMessage={showStatusMessage}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <ResultPhase
                success={success}
                failure={failure}
                statusMessage={statusMessage}
                onClose={handleConfirmationClose}
                eventId={eventId}
                onCancel={onCancel}
                onReschedule={onReschedule}
                showSpamHint={showSpamHint}
            />
            {bookingContent}
        </>
    );
};
